"use client"
/**
 * Keyboard shortcut help overlay (L5).
 * Opens on "?" keypress (when not focused on an input).
 * Renders a modal listing available shortcuts.
 */
import { useEffect, useState } from "react"

const SHORTCUTS = [
  { key: "?",      desc: "Open this help panel" },
  { key: "Esc",   desc: "Close any open dialog" },
  { key: "/ or s", desc: "Focus search bar" },
  { key: "G then H", desc: "Go to Home" },
  { key: "G then L", desc: "Go to Listings" },
  { key: "G then D", desc: "Go to Diaspora page" },
  { key: "G then C", desc: "Go to Contact" },
]

export default function KeyboardHelp() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let gPressed = false
    let gTimer: ReturnType<typeof setTimeout> | null = null

    function handleKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
      const inInput = tag === "input" || tag === "textarea" || tag === "select" || (e.target as HTMLElement)?.isContentEditable

      if (e.key === "Escape") {
        setOpen(false)
        return
      }

      if (inInput) return

      // "?" opens the help modal
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault()
        setOpen(o => !o)
        return
      }

      // "/" or "s" focuses the search bar
      if (e.key === "/" || e.key === "s") {
        e.preventDefault()
        const searchInput = document.querySelector<HTMLInputElement>('input[type="search"], input[placeholder*="earch"], input[name="q"]')
        if (searchInput) {
          searchInput.focus()
          searchInput.select()
        }
        return
      }

      // "g" sequences (g then h = home, g then l = listings, etc.)
      if (e.key.toLowerCase() === "g" && !gPressed) {
        gPressed = true
        if (gTimer) clearTimeout(gTimer)
        gTimer = setTimeout(() => { gPressed = false }, 1000)
        return
      }

      if (gPressed) {
        gPressed = false
        if (gTimer) clearTimeout(gTimer)
        const routes: Record<string, string> = {
          h: "/",
          l: "/listings",
          d: "/diaspora",
          c: "/contact",
        }
        const dest = routes[e.key.toLowerCase()]
        if (dest) {
          e.preventDefault()
          window.location.href = dest
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(27,45,84,0.55)",
          zIndex: 9000,
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9001,
          background: "var(--cream)",
          borderRadius: 20,
          boxShadow: "0 24px 80px rgba(27,45,84,0.25)",
          width: "min(92vw, 440px)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "var(--navy)",
            padding: "20px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, color: "var(--cream)" }}>
            Keyboard shortcuts
          </span>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "rgba(253,251,247,0.6)",
              fontSize: 20,
              lineHeight: 1,
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        {/* Shortcut list */}
        <div style={{ padding: "8px 0 16px" }}>
          {SHORTCUTS.map(s => (
            <div
              key={s.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "11px 28px",
                borderBottom: "1px solid var(--line)",
              }}
            >
              <span style={{ color: "var(--ink)", fontSize: 14 }}>{s.desc}</span>
              <kbd
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "var(--cream-warm)",
                  border: "1px solid var(--line)",
                  borderRadius: 6,
                  padding: "3px 10px",
                  fontFamily: "monospace",
                  fontSize: 13,
                  color: "var(--navy)",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                }}
              >
                {s.key}
              </kbd>
            </div>
          ))}
        </div>

        <div style={{ padding: "12px 28px 20px", textAlign: "center" }}>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Press <kbd style={{ fontFamily: "monospace", fontSize: 11 }}>?</kbd> again or <kbd style={{ fontFamily: "monospace", fontSize: 11 }}>Esc</kbd> to close</span>
        </div>
      </div>
    </>
  )
}
